import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Heading from '../../../generic-components/Heading';
import { ILinkedAccount } from '../../../types';
import { TransferDetails } from './TransferDetails';
import { STYLE, TEXT } from '../constants';
import { TransferDisclaimer } from './TransferDisclaimer';
import { TransferActions } from './TransferActions';
import { takeSnapshot } from '../../../services/snapshot.service';
import { useUploadMoneyTransferConfirmation } from '../../../hooks/mutations/useUploadMoneyTransferConfirmation';
import { useStore } from '../../../store';
import { useAddMoneyMutation } from '../../../hooks/mutations/useAddMoneyMutation';
import { useSendMoneyMutation } from '../../../hooks/mutations/useSendMoneyMutation';
import { FlowContext, UploadContext } from '../pages';
import { trackMixPanel } from '../../../services/mixpanel.service';

const { PAGES: { POST_LINKED: { MONEY_TRANSFER: { CONFIRMATION_MODAL_CONTENT } } } } = STYLE;
const { PAGES: { POST_LINKED: { MONEY_TRANSFER: { USER_MESSAGE } } } } = TEXT;

interface IProps {
  transferValue: string;
  linkAccountData: ILinkedAccount;
  internalCloseModal: () => void;
  estimatedArrival: string;
  firstTimeMessage?: string;
  handleSuccessTransfer: ()=> void;
  handleOnError: (errorData: string)=> void;
}

export const TransferDetailsConfirmation = ({
  transferValue, linkAccountData, internalCloseModal, estimatedArrival, handleSuccessTransfer, handleOnError, firstTimeMessage,
}: IProps) => {
  const isAddMoneyFlow = useContext(FlowContext);
  const uploadContextObj = useContext(UploadContext);
  const [isDisclaimerAgreed, setIsDisclaimerAgreed] = useState<boolean>(false);
  const [isSnapshotActive, setIsSnapshotActive] = useState<boolean>(false);
  const [snapshotFormData, setSnapShotFormData] = useState<unknown>();
  const [transactionId, setTransactionId] = useState<string>('');
  const [addMoneyError, setAddMoneyError] = useState<string>('');
  const { customerId } = useStore(state => state.user);
  const { t } = useTranslation();
  const { mutate: uploadTransferConfirmation, isLoading: isUploadSnapshot } = useUploadMoneyTransferConfirmation({
    fileData: snapshotFormData,
    customerId,
    transactionId,
  }, () => {
    handleSuccessTransfer();
  }, () => {
    internalCloseModal();
  });

  const { mutate: addMoney, isLoading: isLoadingAddMoney } = useAddMoneyMutation({
    customerId,
    linkedAccountId: linkAccountData.externalId,
    amount: transferValue,
  }, (trxId) => {
    setTransactionId(trxId);
  }, (error) => {
    trackMixPanel('View Plaid Result Screen', { Result: 'Failed' });
    setAddMoneyError(error.localizedMessage);
  });

  const { mutate: sendMoney, isLoading: isLoadingSendMoney } = useSendMoneyMutation(
    { customerId, linkedAccountId: linkAccountData.externalId, amount: transferValue },
    (trxId) => {
      setTransactionId(trxId);
    },
    (error) => {
      setAddMoneyError(error.localizedMessage);
    },
  );

  const handleDisclaimerAgreement = (mode: boolean) => {
    setIsDisclaimerAgreed(mode);
  };

  const handleConfirmation = async () => {
    trackMixPanel('Act Add Money Review Sheet', { Action: 'Confirm' });
    setIsSnapshotActive(true);
    const formData: unknown = await takeSnapshot();
    setIsSnapshotActive(false);
    setSnapShotFormData(formData);
    if (isAddMoneyFlow) {
      addMoney();
    } else {
      sendMoney();
    }
  };

  const handleCancel = () => {
    trackMixPanel('Act Add Money Review Sheet', { Action: 'Cancel' });
    internalCloseModal();
  };

  useEffect(() => {
    if (!transactionId) return;
    uploadTransferConfirmation();
    uploadContextObj?.setTransactionId(transactionId);
  }, [transactionId]);

  useEffect(() => {
    if (addMoneyError) {
      handleOnError(addMoneyError);
    }
  }, [addMoneyError]);

  useEffect(() => {
    trackMixPanel('View Add Money Review Sheet');
    if (!isAddMoneyFlow) {
      setIsDisclaimerAgreed(true);
    }
  }, []);

  return (
    <div className={CONFIRMATION_MODAL_CONTENT.WRAPPER}>
      <Heading title={t(USER_MESSAGE.MAIN_HEADING)} className={CONFIRMATION_MODAL_CONTENT.MAIN_HEADING} />
      <TransferDetails firstTimeMessage={firstTimeMessage} estimatedArrival={estimatedArrival} transferValue={transferValue} linkAccountData={linkAccountData} />
      {isAddMoneyFlow && <TransferDisclaimer linkAccountData={linkAccountData} onDisclaimerChange={handleDisclaimerAgreement} />}
      <TransferActions isError={!!addMoneyError} isLoading={isLoadingAddMoney || isLoadingSendMoney || isSnapshotActive || isUploadSnapshot} onCancel={handleCancel} onConfirmation={handleConfirmation} isConfirmable={isDisclaimerAgreed} />
    </div>
  );
};
